// Shared by Cloud Functions and the frontend (imported there as @shared).
//
// A depth chart maps "<pos>_<n>" to a player id, e.g. {pg_0: starter id,
// pg_1: first backup, ...}. A player starts at one position but may back up
// a second one.
//
// autoDepthChart weighs hidden chemistry, so only the server (and admin
// tools) can run it with real data.

import {CHEM_NEUTRAL_FIT, chemistryFit} from "./chemistry.js";
import {positions} from "./positions.js";

export const DEPTH_LEVELS = 4;

// Rough exchange rate between a player's chemistry fit and overall rating
// points at chemistryStrength 1, derived from the engine's chemistry and
// per-point constants: a player whose fit is 0.25 better is worth about 10
// overall points more.
const RATING_POINTS_PER_FIT = 40;

/**
 * True when a player can't play: injured, or listed as unavailable.
 * @param {object} player
 * @return {boolean}
 */
export function isUnavailable(player) {
  return Boolean(player.injury?.gamesOut > 0);
}

/**
 * How valuable a player is to this team: ability plus chemistry fit,
 * expressed in overall rating points.
 * @param {object} player
 * @param {number} teamChem
 * @param {number} [chemStrength] league settings.chemistryStrength
 * @return {number}
 */
export function playerValue(player, teamChem, chemStrength = 1) {
  const fit = chemistryFit(player.chemistry, teamChem);
  return player.overall +
    RATING_POINTS_PER_FIT * chemStrength * (fit - CHEM_NEUTRAL_FIT);
}

/**
 * Checks a depth chart a manager submitted and drops anything unusable.
 * Five different starters are required. Backups are optional, a player may
 * back up more than one position, and injured players may be listed (the
 * engine skips them and falls back to the next player).
 * @param {object} depth {"<pos>_<n>": playerId}
 * @param {object} players the team's players {id: player}
 * @return {object} {depth, errors}: the cleaned chart and what's wrong
 */
export function cleanDepthChart(depth, players) {
  const clean = {};
  const errors = [];
  const starters = new Set();
  for (const pos of positions) {
    const seen = new Set();
    const before = errors.length;
    for (let n = 0; n < DEPTH_LEVELS; n++) {
      const id = depth?.[`${pos.id}_${n}`];
      if (!id) continue;
      if (!players[id]) {
        errors.push(`${pos.short}: that player isn't on this team.`);
      } else if (seen.has(id)) {
        errors.push(`${pos.short}: a player is listed twice.`);
      } else if (n === 0 && starters.has(id)) {
        errors.push(`${pos.short}: a player can only start at one position.`);
      } else {
        seen.add(id);
        if (n === 0) starters.add(id);
        clean[`${pos.id}_${n}`] = id;
      }
    }
    if (!clean[`${pos.id}_0`] && errors.length === before) {
      errors.push(`${pos.short}: choose a starter.`);
    }
  }
  return {depth: clean, errors};
}

/**
 * Who starts the next game, slot by slot (PG..C), the way the engine picks:
 * for each slot, the first healthy player not already chosen from that
 * position's depth chart, then natural position, then secondary position,
 * then anyone, each by overall. Uses visible fields only, so managers can
 * see who will fill in for an injured starter.
 * @param {object} players {id: player}
 * @param {object} depth depth chart
 * @return {string[]} five player ids (fewer if the roster is short)
 */
export function startingLineup(players, depth) {
  const healthy = Object.values(players).filter((p) => !isUnavailable(p));
  const byOverall = [...healthy].sort((a, b) => b.overall - a.overall);
  const lineup = [];
  for (const pos of positions) {
    const listed = [];
    for (let i = 0; i < DEPTH_LEVELS; i++) {
      const p = players[depth?.[`${pos.id}_${i}`]];
      if (p && !isUnavailable(p)) listed.push(p);
    }
    const pick = [
      ...listed,
      ...byOverall.filter((p) => p.pos1 === pos.num),
      ...byOverall.filter((p) => p.pos2 === pos.num),
      ...byOverall,
    ].find((p) => !lineup.includes(p.id));
    if (pick) lineup.push(pick.id);
  }
  return lineup;
}

/**
 * Builds the depth chart an auto-managed team would use.
 * @param {object} players {id: player}
 * @param {number} teamChem
 * @param {number} [chemStrength] league settings.chemistryStrength
 * @return {object} depth chart
 */
export function autoDepthChart(players, teamChem, chemStrength) {
  const healthy = Object.values(players).filter((p) => !isUnavailable(p));
  const value = (p) => playerValue(p, teamChem, chemStrength);
  const byValue = [...healthy].sort((a, b) =>
    value(b) - value(a) ||
    a.id.localeCompare(b.id));
  const depth = {};

  // Starters: best natural fit at each position, then secondary position,
  // then anyone left.
  const starters = new Set();
  for (const pos of positions) {
    const pick =
      byValue.find((p) => !starters.has(p.id) && p.pos1 === pos.num) ??
      byValue.find((p) => !starters.has(p.id) && p.pos2 === pos.num) ??
      byValue.find((p) => !starters.has(p.id));
    if (pick) {
      starters.add(pick.id);
      depth[`${pos.id}_0`] = pick.id;
    }
  }

  // Backups: natural position first, then secondary position.
  for (const pos of positions) {
    const backups = [
      ...byValue.filter((p) => p.pos1 === pos.num),
      ...byValue.filter((p) => p.pos2 === pos.num),
    ].filter((p, i, arr) =>
      !starters.has(p.id) && arr.findIndex((q) => q.id === p.id) === i);
    backups.slice(0, DEPTH_LEVELS - 1).forEach((p, i) => {
      depth[`${pos.id}_${i + 1}`] = p.id;
    });
  }
  return depth;
}
